import { Box, HStack, Tab, TabList, Tabs, useTheme } from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Text from '../../Text';
import { useDevSandbox } from '../DevSandboxProvider';
import useSandboxTheme from '../useSandboxTheme';

const PaneTabs = () => {
  const {
    paneTabs: { tabs },
    mode,
  } = useDevSandbox();

  const { opacity } = useTheme();
  const { colors } = useSandboxTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const currentTabIndex = useMemo(() => {
    return tabs.findIndex((tab) => location.pathname.includes(tab.screenName));
  }, [location.pathname, tabs]);

  useEffect(() => {
    if (!tabs || tabs.length === 0 || mode === 'full-page') {
      return;
    }

    const hasTabInUrl = tabs.some((tab) => location.pathname.includes(tab.screenName));
    const newTabIndex = tabs.findIndex((tab) => location.pathname.includes(tab.screenName));

    if (!hasTabInUrl) {
      navigate(tabs[0].screenName, { replace: true });
    } else if (newTabIndex !== currentTabIndex) {
      navigate(tabs[newTabIndex].screenName, { replace: true });
    }
  }, [location.pathname, currentTabIndex]);

  const handleTabChange = (index) => {
    navigate(tabs[index].screenName);
  };

  // Single tab — skip the tab bar, render content at full height
  if (tabs.length === 1) {
    return (
      <Box data-tour="pane-tabs" width="100%" height="100%">
        {tabs[0].content}
      </Box>
    );
  }

  return (
    <Box data-tour="pane-tabs" width="100%" height="100%">
      <Tabs
        height="43px"
        variant="soft-rounded"
        borderBottom="1px solid"
        borderColor={colors.shellBorder}
        width="100%"
        overflowX="auto"
        index={currentTabIndex}
        onChange={handleTabChange}
      >
        <TabList whiteSpace="nowrap">
          {tabs.map((tab) => (
            <Tab
              key={tab.screenName}
              data-tour={`pane-tab-${tab.screenName}`}
              opacity={opacity.secondary}
              _selected={{ opacity: opacity.primary }}
              _hover={{ opacity: opacity.primary }}
            >
              <HStack>
                {tab.icon}
                <Text opacity={opacity.secondary} _hover={{ opacity: opacity.primary }}>
                  {tab.title}
                </Text>
              </HStack>
            </Tab>
          ))}
        </TabList>
      </Tabs>
      <Box height="calc(100% - 45px)" overflow="auto">
        {tabs.map((tab, index) => (
          <Box
            key={tab.screenName}
            height="100%"
            display={currentTabIndex === index ? 'block' : 'none'}
          >
            {tab.content}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PaneTabs;
